import React, { ChangeEvent, useMemo } from 'react';
import Select from 'react-select';
import {
  IContact,
  IUserProperty,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import Search from 'src/components/Search/Search';
import ContactRowSkeleton from 'src/components/ContactsTable/EmailSelfServiceDialogue/StepPickContacts/skeletons/ContactRowSkeleton';
import Checkbox from 'src/components/Checkbox/Checkbox';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import BulkSelectAllRow from 'src/components/BulkSelectAllRow/BulkSelectAllRow';
import {
  ProjectFilter,
  RoleFilter,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/useEmailSelfServiceFormState';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import { useContactDisplay } from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useContactDisplay';
import './step-pick-contacts.scss';

interface SelectOption {
  label: string;
  value: string;
}

const ALL_ROLES_OPTION: SelectOption = { label: 'All roles', value: 'all' };
const ALL_PROPERTIES_OPTION: SelectOption = {
  label: 'All properties',
  value: 'all',
};

interface StepPickContactsProps {
  roleFilter: RoleFilter;
  projectFilter: ProjectFilter;
  searchQuery: string;
  availableRoles: string[];
  availableProjects: IProperty[];
  filteredContacts: IContact[];
  selectedIds: Set<string>;
  isLoadingContacts: boolean;
  contactsError: string | null;
  onRoleFilterChange: (role: RoleFilter) => void;
  onProjectFilterChange: (projectId: ProjectFilter) => void;
  onSearchQueryChange: (query: string) => void;
  onToggleSelection: (id: string) => void;
  onSetSelection: (ids: string[], selected: boolean) => void;
}

interface ContactRowProps {
  contact: IContact;
  isChecked: boolean;
  onToggle: (id: string) => void;
}

/* CSS custom properties emitted from src/index.scss off the Sass vars in
 * src/styles/includes/variables.scss — single source of truth for both SCSS
 * and TS consumers of the status palette. */
const ALLOW_EMAIL_CHANGE_ENABLED_COLOR: string = 'var(--color-status-positive)';
const ALLOW_EMAIL_CHANGE_DISABLED_COLOR: string = 'var(--color-status-negative)';

const PICK_CONTACTS_SKELETON_ROW_COUNT: number = 6;

const ContactRow = ({ contact, isChecked, onToggle }: ContactRowProps) => {
  const { fullName, roleLabels } = useContactDisplay(contact);

  const propertySummary: string = useMemo(() => {
    const propertyList: IUserProperty[] = contact.properties ?? [];
    const propertyCount: number = propertyList.length;

    if (propertyCount === 1) {
      return propertyList[0].name;
    }

    return `${propertyCount} properties`;
  }, [contact.properties]);

  const isEmailChangeAllowed: boolean = contact.allowEmailChange ?? true;

  const allowEmailChangePillText: string = isEmailChangeAllowed
    ? 'Email change allowed'
    : 'Email change disallowed';

  const allowEmailChangePillColor: string = isEmailChangeAllowed
    ? ALLOW_EMAIL_CHANGE_ENABLED_COLOR
    : ALLOW_EMAIL_CHANGE_DISABLED_COLOR;

  return (
    <label className="spc-row">
      <div className="spc-row-checkbox">
        <Checkbox
          checked={isChecked}
          value={isChecked}
          onChange={() => onToggle(contact.id)}
        />
      </div>

      <div className="spc-row-identity">
        <div className="spc-row-name">{fullName}</div>
        <div className="spc-row-email">{contact.email ?? '—'}</div>
        <div className="spc-row-allow-email-change">
          <CategoryPill
            text={allowEmailChangePillText}
            color={allowEmailChangePillColor}
            size="small"
          />
        </div>
      </div>

      <div className="spc-row-roles">
        {roleLabels.map((roleLabel: string) => (
          <CategoryPill key={roleLabel} text={roleLabel} size="small" />
        ))}
      </div>

      <div className="spc-row-properties">{propertySummary}</div>
    </label>
  );
};

const StepPickContacts = ({
  roleFilter,
  projectFilter,
  searchQuery,
  availableRoles,
  availableProjects,
  filteredContacts,
  selectedIds,
  isLoadingContacts,
  contactsError,
  onRoleFilterChange,
  onProjectFilterChange,
  onSearchQueryChange,
  onToggleSelection,
  onSetSelection,
}: StepPickContactsProps) => {
  const roleOptions: SelectOption[] = [
    ALL_ROLES_OPTION,
    ...availableRoles.map((roleName: string) => ({
      label: roleName,
      value: roleName,
    })),
  ];

  const projectOptions: SelectOption[] = [
    ALL_PROPERTIES_OPTION,
    ...availableProjects.map((project: IProperty) => ({
      label: project.name,
      value: project.id,
    })),
  ];

  const selectedRoleOption: SelectOption =
    roleOptions.find((option: SelectOption) => option.value === roleFilter) ??
    ALL_ROLES_OPTION;

  const selectedProjectOption: SelectOption =
    projectOptions.find(
      (option: SelectOption) => option.value === projectFilter
    ) ?? ALL_PROPERTIES_OPTION;

  const handleRoleChange = (option: SelectOption | null): void => {
    if (option) {
      onRoleFilterChange(option.value as RoleFilter);
    }
  };

  const handleProjectChange = (option: SelectOption | null): void => {
    if (option) {
      onProjectFilterChange(option.value as ProjectFilter);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onSearchQueryChange(event.target.value);
  };

  const filteredIds: string[] = filteredContacts.map(
    (contact: IContact) => contact.id
  );
  const filteredCount: number = filteredContacts.length;

  const allFilteredSelected: boolean =
    filteredCount > 0 &&
    filteredContacts.every((contact: IContact) => selectedIds.has(contact.id));

  const selectedCount: number = selectedIds.size;

  const handleSelectAllToggle = (): void => {
    onSetSelection(filteredIds, !allFilteredSelected);
  };

  const selectAllLabel: string = `Select all visible (${filteredCount})`;
  const selectionMetaLabel: string =
    selectedCount > 0
      ? `${selectedCount} selected`
      : 'No contacts selected';

  const showLoader: boolean = isLoadingContacts;
  const showError: boolean = !isLoadingContacts && Boolean(contactsError);
  const showEmpty: boolean =
    !isLoadingContacts && !contactsError && filteredCount === 0;
  const showList: boolean =
    !isLoadingContacts && !contactsError && filteredCount > 0;

  return (
    <div className="spc-step">
      <div className="spc-header">
        <div className="spc-filters">
          <div className="spc-filter spc-filter-search">
            <Search
              placeholder="Search name, email, or company"
              value={searchQuery}
              onChange={handleSearchChange}
              size={undefined}
              theme={undefined}
            />
          </div>

          <div className="spc-filter spc-filter-select">
            <Select
              value={selectedRoleOption}
              onChange={handleRoleChange}
              options={roleOptions}
              placeholder="Role"
              isSearchable={false}
              classNamePrefix="spc-react-select"
            />
          </div>

          <div className="spc-filter spc-filter-select">
            <Select
              value={selectedProjectOption}
              onChange={handleProjectChange}
              options={projectOptions}
              placeholder="Property"
              isSearchable
              classNamePrefix="spc-react-select"
            />
          </div>
        </div>

        <BulkSelectAllRow
          checked={allFilteredSelected}
          onChange={handleSelectAllToggle}
          label={selectAllLabel}
          disabled={filteredCount === 0}
          rightSlot={selectionMetaLabel}
        />
      </div>

      <div className="spc-list">
        {showLoader &&
          Array.from(
            { length: PICK_CONTACTS_SKELETON_ROW_COUNT },
            (_, index: number) => (
              <ContactRowSkeleton key={`spc-row-skeleton-${index}`} />
            )
          )}

        {showError && (
          <div className="spc-state spc-state-error">
            <div className="spc-state-heading">
              We couldn't load your contacts
            </div>
            <div className="spc-state-body">{contactsError}</div>
          </div>
        )}

        {showEmpty && (
          <div className="spc-state">
            <div className="spc-state-heading">No contacts match</div>
            <div className="spc-state-body">
              Try clearing the role, property, or search filters.
            </div>
          </div>
        )}

        {showList &&
          filteredContacts.map((contact: IContact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              isChecked={selectedIds.has(contact.id)}
              onToggle={onToggleSelection}
            />
          ))}
      </div>
    </div>
  );
};

export default StepPickContacts;
